import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Button from "../../../../componentes/button/button";
import SelecionarContaBpModal from "./SelecionarContaBpModal";
import ImportarRateioModal from "./ImportarRateioModal";
import { formatarMoeda } from "../../../../utils/solicitacoes/formatters";
import { classeSaldo, somaBateComTotal, arredondarMoeda } from "../../../../utils/solicitacoes/orcamento";
import Input from "../../../../componentes/input/input";
import InputMoney from "../../../../componentes/input/inputMoney";
import SaldoProjetadoBpBotao from "../../Shared/SaldoProjetadoBpBotao";

/**
 * comValor=false (Etapa 1 de Compras): apenas seleção informativa das contas BP relevantes, sem valor.
 * comValor=true (Recebimento de Compras, criação de Despesa): cada linha exige um valor a ratear,
 * com soma vs. valorTotal — só nesse modo existe "Importar Rateio" (reaproveitar o padrão percentual
 * de uma solicitação anterior do mesmo fornecedor), já que na Etapa 1 ainda não há valor definido.
 */
export default function RateioOrcamentarioEditor({ itens = [], onChange, filiais, comValor = false, valorTotal, fornecedorId }) {
    const [modalAberto, setModalAberto] = useState(false);
    const [modalImportarAberto, setModalImportarAberto] = useState(false);
    const [recalcular, setRecalcular] = useState(false);
    const selecionarContaBpRef = useRef(null);

    // O painel de seleção de Conta BP fica embutido na página (não é um popup), então ao abri-lo
    // rola até ele para deixar claro que "Adicionar Rateio" e o painel abaixo são a mesma ação.
    function abrirSelecionarContaBp() {
        setModalAberto(true);
        requestAnimationFrame(() => {
            selecionarContaBpRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        });
    }

    // Com nota fiscal, a mesma Conta BP pode se repetir em notas diferentes — a duplicidade real é
    // a combinação Conta BP + Nota Fiscal.
    function chaveItem(item) {
        return comValor ? `${item.baseBpId}|${item.numeroNotaFiscal}` : item.baseBpId;
    }

    function adicionar(conta) {
        if (itens.some(item => chaveItem(item) === chaveItem(conta))) {
            toast.error("Esta conta BP já foi adicionada para esta nota fiscal.");
            return;
        }
        onChange([...itens, conta]);
        toast.success("Conta BP adicionada ao rateio.");
    }

    function atualizarValor(index, valor) {
        onChange(itens.map((item, i) => i === index ? { ...item, valor } : item));
    }

    function atualizarNumeroNota(index, numeroNotaFiscal) {
        onChange(itens.map((item, i) => i === index ? { ...item, numeroNotaFiscal } : item));
    }

    function remover(index) {
        onChange(itens.filter((_, i) => i !== index));
    }

    function abrirImportar() {
        if (!fornecedorId) {
            toast.error("Informe o fornecedor para consultar os rateios anteriores.");
            return;
        }
        setModalImportarAberto(true);
    }

    // Importar substitui o rateio atual pelo padrão percentual da solicitação escolhida — cada
    // linha guarda o percentual usado (`percentualImportado`, invisível na UI) pra permitir
    // recalcular automaticamente se o valor total mudar depois (ver efeito abaixo). Número da nota
    // fiscal sempre entra em branco: é sempre novo, nunca copiado da solicitação anterior.
    function importarRateio(solicitacaoEscolhida) {
        const novoTotal = Number(valorTotal) || 0;
        const novosItens = solicitacaoEscolhida.itens.map(item => ({
            baseBpId: item.baseBpId,
            filialNome: item.filialNome,
            centroCustoCodigo: item.centroCustoCodigo,
            centroCustoDescricao: item.centroCustoDescricao,
            contaReduzido: item.contaReduzido,
            contaContabilDescricao: item.contaContabilDescricao,
            responsavelNome: item.responsavelNome,
            orcamentoMes: item.orcamentoMes,
            valorRealizado: item.valorRealizado,
            saldoAtual: item.saldoAtual,
            numeroNotaFiscal: "",
            valor: arredondarMoeda(Number(item.percentual) * novoTotal),
            percentualImportado: Number(item.percentual),
        }));
        onChange(novosItens);
    }

    // Se o valor total mudar depois de um rateio importado, pergunta se recalcula os valores com
    // base nos mesmos percentuais — ignora a primeira renderização (não é "mudança").
    const valorTotalAnteriorRef = useRef(valorTotal);
    useEffect(() => {
        const anterior = valorTotalAnteriorRef.current;
        valorTotalAnteriorRef.current = valorTotal;
        if (anterior === valorTotal) return;

        const temPercentualImportado = itens.some(item => item.percentualImportado != null);
        if (!temPercentualImportado) return;

        const recalcular = window.confirm(
            "O valor total da solicitação foi alterado. Deseja recalcular os valores do rateio com base nos mesmos percentuais importados?"
        );
        if (!recalcular) return;

        const novoTotal = Number(valorTotal) || 0;
        onChange(itens.map(item => item.percentualImportado != null
            ? { ...item, valor: arredondarMoeda(item.percentualImportado * novoTotal) }
            : item));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recalcular]);

    const totalRateado = itens.reduce((soma, item) => soma + (Number(item.valor) || 0), 0);
    const totalConfere = somaBateComTotal(totalRateado, valorTotal);

    return (
        <div>
            <div ref={selecionarContaBpRef}>
                <SelecionarContaBpModal
                    isOpen={modalAberto}
                    onClose={() => setModalAberto(false)}
                    filiais={filiais}
                    onSelecionar={adicionar}
                    idsJaAdicionados={itens.map(chaveItem)}
                    comValor={comValor}
                />
            </div>
            <div className="flex flex-col gap-3">

                {itens.length > 0 && (
                    <div className="overflow-x-auto border border-border rounded-lg overflow-hidden">
                        <table className="w-full text-xs">
                            <thead className="bg-muted">
                                <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                                    {comValor && <th className="px-3 py-2 font-semibold w-28">Nota Fiscal</th>}
                                    <th className="px-3 py-2 font-semibold">Filial</th>
                                    <th className="px-3 py-2 font-semibold">C. Custo</th>
                                    <th className="px-3 py-2 font-semibold">Conta</th>
                                    <th className="px-3 py-2 font-semibold text-right">Responsável</th>
                                    <th className="px-3 py-2 font-semibold text-left">Orçamento</th>
                                    {comValor && <th className="px-3 py-2 font-semibold text-left min-w-36">Valor a ratear</th>}
                                    <th className="px-3 py-2 w-16" />
                                </tr>
                            </thead>
                            <tbody>
                                {itens.map((item, index) => (
                                    <tr key={`${item.baseBpId}-${index}`} className="border-t border-border">
                                        {comValor && (
                                            <td className="px-3 py-2 min-w-24">
                                                <Input
                                                    type="text"
                                                    value={item.numeroNotaFiscal ?? ""}
                                                    onChange={e => atualizarNumeroNota(index, e.target.value)}
                                                />
                                            </td>
                                        )}
                                        <td className="px-3 py-2 text-foreground">{item.filialNome}</td>
                                        <td className="px-3 py-2 text-foreground">{item.centroCustoCodigo} · {item.centroCustoDescricao}</td>
                                        <td className="px-3 py-2 text-foreground">{item.contaReduzido} · {item.contaContabilDescricao}</td>
                                        <td className="px-3 py-2 text-foreground">{item.responsavelNome}</td>
                                        <td className="px-3 py-2 text-left text-foreground">
                                            {formatarMoeda(item.orcamentoMes)}
                                            <br />
                                            {formatarMoeda(item.valorRealizado)}
                                            <br />
                                            <span className={`text-left font-semibold ${classeSaldo(item.saldoAtual, item.orcamentoMes)}`}>
                                                {formatarMoeda(item.saldoAtual)}
                                            </span>
                                        </td>

                                        {comValor && (
                                            <td className="px-3 py-2 text-left min-w-48">
                                                <div className="flex items-center justify-end gap-2">
                                                    <InputMoney
                                                        minFractionDigits={2}
                                                        maxFractionDigits={2}
                                                        mode="decimal"
                                                        onFocus={(e) => e.target.select()}
                                                        value={item.valor}
                                                        onChange={e => atualizarValor(index, e.target.value)}
                                                    />
                                                    <span className="w-12 text-[11px] text-muted-foreground text-right">
                                                        {totalRateado > 0 ? `${((Number(item.valor) || 0) / totalRateado * 100).toFixed(1)}%` : "—"}
                                                    </span>
                                                </div>
                                            </td>
                                        )}
                                        <td className="px-3 py-2 text-center whitespace-nowrap">
                                            <SaldoProjetadoBpBotao baseBpId={item.baseBpId} />
                                            <button
                                                type="button"
                                                title="Remover"
                                                onClick={() => remover(index)}
                                                className="h-7 w-7 inline-flex items-center justify-center rounded-full text-destructive hover:bg-destructive/10"
                                            >
                                                <i className="fa fa-trash" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {comValor && (
                            <div className={`px-3 py-2 text-sm font-medium border-t border-border ${totalConfere ? "text-success" : "text-destructive"}`}>
                                Total rateado: {formatarMoeda(totalRateado)}
                                {Number(valorTotal) > 0 && ` / ${formatarMoeda(valorTotal)}`}
                            </div>
                        )}
                    </div>
                )}

                <div className="flex flex-wrap gap-2">
                    <Button variant="outline" label="Adicionar Rateio" iconLeft="fa fa-plus" onClick={abrirSelecionarContaBp} />
                    {comValor && (
                        <Button variant="outline" label="Importar Rateio" iconLeft="fa fa-history" onClick={abrirImportar} />
                    )}
                    {comValor && (
                        <Button variant="outline" label="Recalcular" iconLeft="fa fa-calculator" onClick={() => setRecalcular(!recalcular)} />
                    )}
                </div>

                {comValor && (
                    <ImportarRateioModal
                        isOpen={modalImportarAberto}
                        onClose={() => setModalImportarAberto(false)}
                        fornecedorId={fornecedorId}
                        onImportar={importarRateio}
                    />
                )}
            </div>
        </div>
    );
}
